import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { useDispatch, shallowEqual } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Tooltip } from 'antd';
import axios from 'axios';
import PubSub from 'pubsub-js';

// GLOBAL COMPONENTS
import styled from '@emotion/styled';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faMobile } from '@fortawesome/pro-regular-svg-icons';
import { faMapMarkerAlt } from '@fortawesome/pro-solid-svg-icons';

import { PROFILE_TYPES } from 'lib/constants';
import { fetchMedia, parseBoolean, buildPhoneMask } from 'lib/helper';

// ACTIONS & HOOKS
import { useSelector } from 'store';
import { UsersActions } from 'store/types/types';
import { fetchNetworkRecognitions } from 'store/reducers/recognitionReducer';
// import { canUseFunction } from 'store/selectors/authSelectors';
// import { openChatRoom } from 'store/actions/chatActions';
import useAuth from 'hooks/useAuth';
import { useFeatureToggle } from 'hooks';

// LOCAL COMPONENTS
import Avatar from '../layout/Common/ProfileImage/ProfileImage';

//#region TYPES
type UserInfoProps = UserInfo & UserContacts & { visible?: boolean };

interface CreditStatus {
  total: number;
  used: number;
  remaining: number;
}
//#endregion

//#region STYLES (INSPIRADOS NO USERCOVER)
const CardContainer = styled.div`
  width: 100%;
  min-width: 340px;
  max-width: 460px;
  padding: 16px;
  background: #ffffff;
  border-radius: 16px;
  // box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial,
    sans-serif;
  box-sizing: border-box;
`;

const HeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const UserName = styled.div`
  font-size: 17px;
  font-weight: 700;
  color: #111111;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserSubTitle = styled.div`
  font-size: 13px;
  color: #666666;
  margin-top: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// Estilo padronizado com o UserCover
const CompetenciesWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 14px;
`;

const CompetencyTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background-color: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  font-size: 13px;
  color: #333333;
  font-weight: 500;
  line-height: 1.2;

  img {
    width: 18px;
    height: 18px;
    object-fit: contain;
    flex-shrink: 0;
  }

  span.count {
    font-weight: 600;
    color: #111111;
  }

  span.name {
    color: #444444;
  }
`;

const ContactInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

const ContactRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #333333;

  .icon-wrapper {
    width: 18px;
    display: flex;
    justify-content: center;
    align-items: center;
    color: #666666;
    font-size: 14px;
  }

  a {
    color: ${({ theme }) => theme.colors.primary || '#1890ff'};
    text-decoration: underline;

    &:hover {
      opacity: 0.8;
    }
  }
`;

const ActionButtonsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const PrimaryBtn = styled(Link)`
  flex: 1;
  height: 38px;
  padding: 0 8px;
  background-color: ${({ theme }) => theme.colors.primary} !important;
  color: #ffffff !important;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none !important;
  white-space: nowrap;
  box-sizing: border-box;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
    color: #ffffff !important;
  }
`;

const SecondaryBtn = styled.button`
  flex: 1;
  height: 38px;
  padding: 0 8px;
  background: #ffffff;
  border: 1px solid ${({ theme }) => theme.colors.primary || '#e0e0e0'};
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary || '#111111'};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  box-sizing: border-box;
  transition: all 0.2s ease;

  &:hover {
    background: #fafafa;
    opacity: 0.85;
  }
`;

const SecondaryLinkBtn = styled(Link)`
  flex: 1;
  height: 38px;
  padding: 0 8px;
  background: #ffffff;
  border: 1px solid ${({ theme }) => theme.colors.primary || '#e0e0e0'};
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary || '#111111'};
  text-decoration: none !important;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  box-sizing: border-box;
  transition: all 0.2s ease;

  &:hover {
    background: #fafafa;
    color: ${({ theme }) => theme.colors.primary || '#111111'} !important;
    opacity: 0.85;
  }
`;

const DisabledSecondaryBtn = styled.button`
  flex: 1;
  width: 100%;
  height: 38px;
  padding: 0 8px;
  background: #f5f5f5;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.25);
  cursor: not-allowed;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  box-sizing: border-box;
`;
//#endregion

const UserInfo: React.VFC<UserInfoProps> = ({
  user,
  occupation,
  department,
  companyUnit,
  phone,
  workphoneExtension,
  follow,
  online,
  visible,
}) => {
  const { t } = useTranslation();
  const { userInfo, token, networkSettings } = useAuth();
  const [recognitionFeature] = useFeatureToggle('recognition');
  const isRecognitionEnabled = Boolean(networkSettings?.enableRecognition);
  const dispatch: AppDispatch = useDispatch();

  const [following, setFollowing] = useState(follow);
  const [profileRecognitions, setProfileRecognitions] = useState<any[]>([]);
  const [creditStatus, setCreditStatus] = useState<CreditStatus | null>(null);

  // SELETORES DO REDUX
  // const chatFunction = useSelector(state => canUseFunction(state, 'chat'), shallowEqual);
  const mailViewHide = useSelector(store =>
    parseBoolean(store.auth.featureToogle['mail_view_hide'])
  );

  const networkRecognitions = useSelector(
    (state: any) => state.recognition?.networkRecognitions || [],
    shallowEqual
  );

  const isSelf = user.userId === userInfo?.user?.userId;

  // 1. Garante que as competências globais da rede estejam no Redux
  useEffect(() => {
    if (recognitionFeature && networkRecognitions.length === 0) {
      dispatch(fetchNetworkRecognitions());
    }
  }, [recognitionFeature, networkRecognitions.length, dispatch]);

  // 2. Busca os reconhecimentos atribuídos ao usuário e ouve eventos de recarga
  const fetchProfileRecognitions = useCallback(async () => {
    if (!recognitionFeature || !user?.userId) return;
    try {
      const response = await axios.get('/api/recognition/getRecognitionsByUser', {
        params: { userId: user.userId },
      });
      if (response.data) {
        setProfileRecognitions(response.data);
      }
    } catch (error) {
      console.error('Erro ao buscar competências no tooltip:', error);
    }
  }, [recognitionFeature, user?.userId]);

  // Busca dados frescos toda vez que o tooltip é aberto, não só no primeiro hover
  // (o card fica montado entre aberturas, então sem isso os badges ficavam presos
  // no snapshot do primeiro fetch)
  useEffect(() => {
    if (visible) fetchProfileRecognitions();
  }, [visible, fetchProfileRecognitions]);

  // Mantém os badges atualizados quando um reconhecimento é dado na mesma sessão/aba
  useEffect(() => {
    const subToken = PubSub.subscribe('RELOAD_RECOGNITIONS', fetchProfileRecognitions);
    return () => {
      PubSub.unsubscribe(subToken);
    };
  }, [fetchProfileRecognitions]);

  // 3. Busca o saldo de créditos do usuário logado
  const fetchUserCredits = useCallback(async () => {
    if (!recognitionFeature || isSelf) return;
    try {
      const response = await axios.get<CreditStatus>('/api/recognition/getUserCreditStatus');
      setCreditStatus(response.data);
    } catch (error) {
      console.error('Erro ao buscar saldo de créditos no tooltip:', error);
    }
  }, [recognitionFeature, isSelf]);

  useEffect(() => {
    if (visible) fetchUserCredits();
  }, [visible, fetchUserCredits]);

  useEffect(() => {
    const subToken = PubSub.subscribe('RELOAD_RECOGNITIONS', fetchUserCredits);
    return () => {
      PubSub.unsubscribe(subToken);
    };
  }, [fetchUserCredits]);

  const getSrc = useCallback(
    (imageId: number) => {
      if (!imageId) return '';
      return fetchMedia(imageId, token!);
    },
    [token]
  );

  // Agrupa e conta cada reconhecimento recebido
  const groupedRecognitions = useMemo(() => {
    return profileRecognitions.reduce((acc, curr) => {
      const recId = curr.recognitionId || curr.RecognitionId;
      acc[recId] = (acc[recId] || 0) + 1;
      return acc;
    }, {} as Record<number, number>);
  }, [profileRecognitions]);

  const visibleRecognitions = useMemo(() => {
    const entries = Object.entries(groupedRecognitions) as [string, number][];
    return entries.filter(([_, count]) => count >= 1);
  }, [groupedRecognitions]);

  const dispatchUser = useCallback(
    followState => {
      dispatch({
        type: UsersActions.FETCH_USER,
        payload: {
          userId: user.userId,
          user: {
            user,
            occupation,
            department,
            companyUnit,
            follow: followState,
            online,
          },
        },
      });
    },
    [dispatch, user, occupation, department, companyUnit, online]
  );

  const sendFollow = useCallback(() => {
    if (!following) {
      axios.post(`/api/Profile/followUser?userToFollow=${user.userId}`).then(() => {
        setFollowing(true);
        dispatchUser(true);
      });
    } else {
      axios.delete(`/api/Profile/unfollowUser?userToUnfollow=${user.userId}`).then(() => {
        setFollowing(false);
        dispatchUser(false);
      });
    }
  }, [following, user.userId, dispatchUser]);

  // const handleOpenChat = useCallback(() => {
  //   dispatch(openChatRoom({ userId: user.userId }));
  // }, [dispatch, user.userId]);

  const isOutOfCredits = creditStatus && creditStatus.remaining <= 0;

  return (
    <CardContainer>
      {/* HEADER */}
      <HeaderWrapper>
        <Avatar
          profile={{
            profileId: user.userId,
            name: user.name,
            profileType: PROFILE_TYPES.user,
            mediaId: user.mediaId,
          }}
          size="lg"
        />
        <HeaderInfo>
          <UserName>{user.name}</UserName>
          {(occupation || department) && (
            <UserSubTitle>
              {occupation?.description}
              {occupation && (department || companyUnit) ? ' | ' : ''}
              {department?.description || companyUnit?.description}
            </UserSubTitle>
          )}
        </HeaderInfo>
      </HeaderWrapper>

      {/* RECONHECIMENTOS / COMPETÊNCIAS (Estrutura e CSS idênticos ao UserCover) */}
      {recognitionFeature && isRecognitionEnabled && visibleRecognitions.length > 0 && (
        <CompetenciesWrapper>
          {visibleRecognitions.map(([idStr, count]) => {
            const recId = Number(idStr);
            const networkRec = networkRecognitions.find((n: any) => n.recognitionId === recId);

            if (!networkRec) return null;

            return (
              <CompetencyTag key={recId}>
                {networkRec.iconMediaId ? (
                  <img src={getSrc(networkRec.iconMediaId)} alt={networkRec.name} />
                ) : null}
                <span className="count">{count}x</span>
                <span className="name">{networkRec.name}</span>
              </CompetencyTag>
            );
          })}
        </CompetenciesWrapper>
      )}

      {/* CONTATOS & LOCALIZAÇÃO */}
      <ContactInfoWrapper>
        {(mailViewHide || user.email) && (
          <ContactRow>
            <div className="icon-wrapper">
              <FontAwesomeIcon icon={faEnvelope} />
            </div>
            <a href={`mailto:${user.email}`}>{user.email}</a>
          </ContactRow>
        )}

        {companyUnit && (
          <ContactRow>
            <div className="icon-wrapper">
              <FontAwesomeIcon icon={faMapMarkerAlt} />
            </div>
            <span>{companyUnit.description}</span>
          </ContactRow>
        )}

        {phone && (
          <ContactRow>
            <div className="icon-wrapper">
              <FontAwesomeIcon icon={faMobile} />
            </div>
            <span>{buildPhoneMask(phone)}</span>
          </ContactRow>
        )}

        {workphoneExtension && (
          <ContactRow>
            <div className="icon-wrapper" style={{ fontWeight: 600 }}>
              #
            </div>
            <span>
              {t('common.phoneExtension', 'Ramal')}: {workphoneExtension}
            </span>
          </ContactRow>
        )}
      </ContactInfoWrapper>

      {/* OS 4 BOTÕES DE AÇÃO */}
      <ActionButtonsRow>
        <PrimaryBtn to={`/users/${user.userId}`}>
          {t('common.viewProfile', 'Ver perfil')}
        </PrimaryBtn>

        {recognitionFeature && !isSelf && (
          <>
            {isRecognitionEnabled &&
              (isOutOfCredits ? (
                <Tooltip
                  title={
                    <div style={{ textAlign: 'center', lineHeight: '1.4' }}>
                      Você atingiu seu limite de créditos. Novas
                      <br />
                      competências poderão ser enviadas a partir da
                      <br />
                      próxima renovação trimestral (01/01, 01/04, 01/07 ou 01/10).
                    </div>
                  }
                  color="#fff"
                  overlayInnerStyle={{
                    color: '#333',
                    padding: '12px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  }}
                >
                  <span style={{ flex: 1, display: 'inline-block', cursor: 'not-allowed' }}>
                    <DisabledSecondaryBtn disabled style={{ pointerEvents: 'none' }}>
                      {t('recognition.recognize', 'Reconhecer')}
                    </DisabledSecondaryBtn>
                  </span>
                </Tooltip>
              ) : (
                <SecondaryLinkBtn to={`/users/${user.userId}`}>
                  {t('recognition.recognize', 'Reconhecer')}
                </SecondaryLinkBtn>
              ))}

            {/* {chatFunction && (
              <SecondaryBtn onClick={handleOpenChat}>{t('common.openChat', 'Chat')}</SecondaryBtn>
            )} */}

            <SecondaryBtn onClick={sendFollow}>
              {following ? t('follow.unfollow', 'Deixar de seguir') : t('follow.follow', 'Seguir')}
            </SecondaryBtn>
          </>
        )}
      </ActionButtonsRow>
    </CardContainer>
  );
};

export default UserInfo;
